const EARTH_RADIUS_M = 6_371_000;

export interface LatLng {
  lat: number;
  lng: number;
}

export function haversineMeters(a: LatLng, b: LatLng): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Bearing from a → b in degrees (0 = north, clockwise). */
export function bearingDegrees(a: LatLng, b: LatLng): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const toDeg = (r: number) => (r * 180) / Math.PI;
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const dLng = toRad(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/** Simple flat-earth offset from heading (deg) and speed (m/s) over dt seconds. */
export function offsetByHeadingSpeed(
  point: LatLng,
  headingDeg: number,
  speedMs: number,
  dtSeconds: number,
): LatLng {
  const dist = speedMs * dtSeconds;
  const headingRad = (headingDeg * Math.PI) / 180;
  const dLat = (dist * Math.cos(headingRad)) / EARTH_RADIUS_M;
  const dLng =
    (dist * Math.sin(headingRad)) /
    (EARTH_RADIUS_M * Math.cos((point.lat * Math.PI) / 180));
  return {
    lat: point.lat + (dLat * 180) / Math.PI,
    lng: point.lng + (dLng * 180) / Math.PI,
  };
}

/** Progress 0–1 along straight line from pickup to delivery by current position. */
export function routeProgress(
  pickup: LatLng,
  delivery: LatLng,
  current: LatLng,
): number {
  const total = haversineMeters(pickup, delivery);
  if (total < 1) return 1;
  const fromStart = haversineMeters(pickup, current);
  return Math.min(1, Math.max(0, fromStart / total));
}
